<template>
  <div>
    <div class="modal" :class="{ 'is-active': emailModalShown }">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="box">
          <template v-if="emailResult.has">
            <div class="notification" :class="{ 'is-danger': !emailResult.success, 'is-success': emailResult.success }">
              {{ emailResult.success ? "L'envoi s'est effectué avec succès." : "Une erreur est survenue lors de l'envoi. Merci de rééssayer." }}
            </div>
            <p class="control">
              <button class="button is-primary" @click.prevent="resetEmailModal">Fermer</button>
            </p>
          </template>
          <template v-else>
            <label class="label">E-mail(s)</label>
            <div v-for="email, index in emails" class="control is-grouped">
              <p class="control is-expanded">
                <input v-model="emails[index]" class="input" type="email" :placeholder="`E-mail ${index + 1}`">
              </p>
              <p class="control">
                <a class="button is-danger" @click.prevent="deleteRecipient(index)">Supprimer</a>
              </p>
            </div>

            <div class="control is-grouped">
              <p class="control">
                <button class="button is-primary is-outlined" @click.prevent="addRecipient">Ajouter un destinataire</button>
              </p>
              <p class="control">
                <button class="button is-primary" @click.prevent="sendEmail" :class="{ 'is-loading': emailOngoing }" :disabled="emailOngoing">Envoyer</button>
              </p>
              <p class="control">
                <button class="button is-link" @click.prevent="resetEmailModal">Annuler</button>
              </p>
            </div>
          </template>
        </div>
      </div>
      <button class="modal-close" @click.prevent="emailModalShown = false"></button>
    </div>

    <div class="notification">
      <strong>Fonctionnement : </strong> vous allez créer un <i>dossier</i>. Un dossier est simplement un conteneur
      dans lequel seront stockés vos fichiers.
    </div>

    <template v-if="step === STEPS.NAME">
      <label class="label">Nom du dossier</label>
      <p class="control">
        <input v-model="directoryName" class="input" type="text" placeholder="Nom">
      </p>
      <div class="block">
        <a class="button is-primary" @click.prevent="createDirectory">Créer le dossier</a>
      </div>
    </template>

    <span v-show="step === STEPS.UPLOAD">
      <div id="dropzone" ref="dropzone">
        <h3 class="dz-message">Cliquez ou glissez-déposez un fichier</h3>
      </div>
      <ul>
        <li>
          Adresse du dossier<br>
          <code><a :href="`${rootUrl}/dossier/${directoryId}`" id="folder-address" target="_blank">{{ rootUrl }}/dossier/{{ directoryId }}</a></code>
          <a class="button is-small copiable" data-clipboard-target="#folder-address">
            <span>Copier</span>
          </a>
        </li>
        <li v-for="file in addedFiles">
          {{ file.file.name }} ({{ file.size }})
          <template v-if="file.done">
            <span class="tag is-success">Envoyé</span>
            <a class="button is-small is-danger is-outlined" @click.prevent="deleteFile(file.fileName)">
              <span>Supprimer</span>
            </a>
            <br>
            <code><a :href="`${rootUrl}/dossier/${directoryId}/${file.fileName}`" :id="`file-${directoryId}-address`" target="_blank">{{rootUrl}}/dossier/{{directoryId}}/{{ file.fileName }}</a></code>
            <a class="button is-small copiable" :data-clipboard-target="`#file-${directoryId}-address`">
              <span>Copier</span>
            </a>
          </template>
          <template v-else>
            <span class="tag is-dark">Envoi en cours...</span><br>
            <progress class="progress is-primary" :value="file.progress" max="100">{{ file.progress }}%</progress>
          </template>
        </li>
      </ul>
      <div class="block">
        <a class="button is-primary" @click.prevent="emailModalShown = true">Envoyer par e-mail</a>
      </div>
      <span></span> <!-- above block does not have a margin bottom if last elem -->
    </span>
  </div>
</template>

<script>
  import Clipboard from 'clipboard'
  import Dropzone from 'dropzone'
  import axios from 'axios'

  import {getRootUrl} from '../../helpers/url'

  const STEPS = {
    NAME: 'NAME',
    UPLOAD: 'UPLOAD'
  }

  export default {
    data () {
      return {
        rootUrl: getRootUrl(),
        STEPS,
        step: STEPS.NAME,
        directoryName: '',
        directoryId: '',
        clipboard: null,
        dropzone: null,
        addedFiles: [],
        emailModalShown: false,
        emailOngoing: false,
        emailResult: {
          has: false,
          success: false
        },
        emails: ['']
      }
    },
    mounted () {
      this.clipboard = new Clipboard('.copiable')
      this.clipboard.on('success', function (e) {
        e.clearSelection()

        e.trigger.innerHTML = 'Copié'
        setTimeout(() => { e.trigger.innerHTML = 'Copier' }, 2000)
      })
      Dropzone.autoDiscover = false
      this.dropzone = new Dropzone(this.$refs.dropzone, {
        url: `${this.rootUrl}/api/upload-file`,
        addedfile: function (file) { } // disable the preview
      })
      this.dropzone.on('addedfile', (file) => {
        this.addedFiles.push({
          file,
          progress: 0,
          done: false,
          fileName: '',
          size: 0
        })
      })

      this.dropzone.on('sending', (file, xhr, formData) => {
        formData.append('directoryId', this.directoryId)
      })

      this.dropzone.on('uploadprogress', (file, percentage) => {
        this.addedFiles.find(addedFile => addedFile.file === file).progress = percentage
      })

      this.dropzone.on('success', (file, res) => {
        this.addedFiles.find(addedFile => addedFile.file === file).fileName = res.fileName
        this.addedFiles.find(addedFile => addedFile.file === file).size = res.size
        this.addedFiles.find(addedFile => addedFile.file === file).done = true
      })
    },
    methods: {
      async createDirectory () {
        const res = await axios.post(`${this.rootUrl}/api/create-directory`, {
          directoryName: this.directoryName
        }, {
          headers: { 'Content-Type': 'application/json' }
        })

        this.directoryId = res.data.directoryId
        this.step = STEPS.UPLOAD
      },
      async deleteFile (fileName) {
        await axios.delete(`${this.rootUrl}/api/dossier/${this.directoryId}/${fileName}`, {
          headers: { 'Content-Type': 'application/json' }
        })

        const addedFileIndex = this.addedFiles.findIndex(addedFile => addedFile.fileName === fileName)
        this.addedFiles.splice(addedFileIndex, 1)
      },
      resetEmailModal () {
        this.emailModalShown = false
        this.emails = ['']
        this.emailOngoing = false
        this.emailResult.has = false
      },
      addRecipient () {
        this.emails.push('')
      },
      deleteRecipient (index) {
        this.emails.splice(index, 1)
      },
      async sendEmail () {
        this.emailOngoing = true

        const emails = this.emails.filter(email => email !== '')

        try {
          await axios.post(`${this.rootUrl}/api/directory/${this.directoryId}/send-email`, {
            recipients: emails
          }, {
            headers: { 'Content-Type': 'application/json' }
          })

          this.emailResult.success = true
        } catch (err) {
          this.emailResult.success = false
        } finally {
          this.emailResult.has = true
          this.emailOngoing = false
        }
      }
    }
  }
</script>

<style lang="sass" scoped>
  #dropzone
    width: 100%
    height: 100px

    background-color: #fff
    border: 2px dashed
    border-radius: 10px

    h3
      line-height: 100px

  ul
    li
      margin-bottom: 20px

      list-style-type: none

      &:last-of-type
        margin-bottom: 0
</style>



// WEBPACK FOOTER //
// Home.vue?4d4ee2e6